import apiClient from "@/api/client";
import { uploadManagedFile } from "@/utils/managedFile";
import type {
  LineItemChange,
  ManagedFileBrief,
  OrderChangeRequest,
  OrderChangeRequestType,
  WpwOwnerChangeInput,
  WpwScope,
} from "@/features/orders/types";

const wrap = <T>(res: { data: { data: T } }) => res.data.data;

export interface LineItemOverride {
  order_line_item_id: string | null;  // null = new item to add
  line_item_type_id?: string;         // required when order_line_item_id is null
  new_quantity?: string;
  new_unit_rate?: string;
}

export const orderChangesApi = {
  list: (params?: Record<string, string>) =>
    apiClient.get<{ data: OrderChangeRequest[] }>("/orders/change-requests/", { params }).then(wrap),

  get: (id: string) =>
    apiClient.get<{ data: OrderChangeRequest }>(`/orders/change-requests/${id}/`).then(wrap),

  create: (data: {
    order: string;
    change_type: OrderChangeRequestType;
    description: string;
    new_deadline?: string | null;
    new_description?: string;
    line_item_changes?: LineItemChange[];
    wpw_owner_changes?: WpwOwnerChangeInput[];
  }) =>
    apiClient.post<{ data: OrderChangeRequest }>("/orders/change-requests/", data).then(wrap),

  update: (id: string, data: Partial<OrderChangeRequest>) =>
    apiClient.patch<{ data: OrderChangeRequest }>(`/orders/change-requests/${id}/`, data).then(wrap),

  delete: (id: string) =>
    apiClient.delete(`/orders/change-requests/${id}/`),

  // `line_item_overrides` is a GK-only field (the director may amend proposed values).
  // The contractor approves the request as proposed, and the backend rejects any
  // overrides from them with 400 — so the key is sent only when there is something in it.
  // `wpw_owner_changes` (GK) replaces the WPW list remembered with the request; left out,
  // the list filed with the request is applied.
  approve: (
    id: string,
    comment?: string,
    line_item_overrides?: LineItemOverride[],
    wpw_owner_changes?: WpwOwnerChangeInput[],
  ) =>
    apiClient
      .post<{ data: OrderChangeRequest }>(`/orders/change-requests/${id}/approve/`, {
        comment: comment ?? "",
        ...(line_item_overrides && line_item_overrides.length > 0 ? { line_item_overrides } : {}),
        ...(wpw_owner_changes ? { wpw_owner_changes } : {}),
      })
      .then(wrap),

  // Lista WPW zamówienia dla formularza (tylko GK): pozycja liczby WPW, bieżące WPW,
  // właściciele do dodania z pakietem.
  wpwScope: (orderId: string) =>
    apiClient.get<{ data: WpwScope }>(`/orders/${orderId}/wpw-scope/`).then(wrap),

  // Plik ze zgodą do pozyskania — wgrywany do rejestru plików, we wniosku idzie jego id.
  uploadFile: (file: File) => uploadManagedFile<ManagedFileBrief>(file),

  reject: (id: string, comment?: string) =>
    apiClient.post<{ data: OrderChangeRequest }>(`/orders/change-requests/${id}/reject/`, { comment: comment ?? "" }).then(wrap),

  withdraw: (id: string) =>
    apiClient.post<{ data: OrderChangeRequest }>(`/orders/change-requests/${id}/withdraw/`).then(wrap),
};
